import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PRESETS = [
  { id: "gmail", label: "Gmail / Workspace", host: "smtp.gmail.com", port: 587, security: "starttls" },
  { id: "office365", label: "Microsoft 365", host: "smtp.office365.com", port: 587, security: "starttls" },
  { id: "aruba", label: "Aruba", host: "smtps.aruba.it", port: 465, security: "ssl" },
  { id: "register", label: "Register.it", host: "authsmtp.securemail.pro", port: 465, security: "ssl" },
];

export const Section = ({ title, hint, children, testId }) => (
  <section className="panel space-y-5 p-6" data-testid={testId}>
    <div>
      <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
      {hint && <p className="mt-1 text-sm text-slate-500">{hint}</p>}
    </div>
    {children}
  </section>
);

const F = ({ id, label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><Label htmlFor={id}>{label}</Label>{children}</div>
);

export const CompanySection = ({ form, set }) => (
  <Section title="Azienda" testId="settings-company-section">
    <F id="company" label="Nome azienda (usato come {AZIENDA})">
      <Input id="company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="company-name-input" />
    </F>
  </Section>
);

export const SmtpSection = ({ form, set }) => {
  const applyPreset = (p) => { set("smtp_host", p.host); set("smtp_port", p.port); set("smtp_security", p.security); };
  return (
    <Section title="Server di invio (SMTP)" testId="settings-smtp-section"
      hint="Compilati automaticamente quando colleghi la casella. Modificali solo se il tuo provider usa server diversi.">
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button key={p.id} type="button" onClick={() => applyPreset(p)} data-testid={`smtp-preset-${p.id}`}
            className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white">
            {p.label}
          </button>
        ))}
      </div>
      {/gmail|google/i.test(form.smtp_host) && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="gmail-app-password-hint">
          Con Gmail la password normale <b>non funziona</b>: attiva la verifica in due passaggi e crea una{" "}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline">password per le app</a>{" "}
          (16 lettere) da inserire qui sotto. Utente = indirizzo Gmail completo, uguale all'email mittente.
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
        <F id="host" label="Server SMTP" className="sm:col-span-3">
          <Input id="host" value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.tuaazienda.it" data-testid="smtp-host-input" />
        </F>
        <F id="port" label="Porta" className="sm:col-span-1">
          <Input id="port" type="number" value={form.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} data-testid="smtp-port-input" />
        </F>
        <F id="security" label="Sicurezza" className="sm:col-span-2">
          <Select value={form.smtp_security} onValueChange={(v) => set("smtp_security", v)}>
            <SelectTrigger id="security" data-testid="smtp-security-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="starttls">STARTTLS (587)</SelectItem>
              <SelectItem value="ssl">SSL/TLS (465)</SelectItem>
              <SelectItem value="none">Nessuna</SelectItem>
            </SelectContent>
          </Select>
        </F>
        <F id="user" label="Utente" className="sm:col-span-3">
          <Input id="user" value={form.smtp_user} onChange={(e) => set("smtp_user", e.target.value)} autoComplete="off" placeholder={form.sender_email || "di solito l'indirizzo email completo"} data-testid="smtp-user-input" />
        </F>
        <F id="smtppw" label="Password" className="sm:col-span-3">
          <Input id="smtppw" type="password" value={form.smtp_password} onChange={(e) => set("smtp_password", e.target.value)} autoComplete="new-password"
            placeholder={form.has_smtp_password ? "•••••••• (salvata, lascia vuoto per non cambiarla)" : ""} data-testid="smtp-password-input" />
        </F>
        <F id="sender" label="Email mittente" className="sm:col-span-3">
          <Input id="sender" type="email" value={form.sender_email} onChange={(e) => set("sender_email", e.target.value)} placeholder="paghe@tuaazienda.it" data-testid="smtp-sender-email-input" />
        </F>
        <F id="sendername" label="Nome mittente" className="sm:col-span-3">
          <Input id="sendername" value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} placeholder="Ufficio Paghe" data-testid="smtp-sender-name-input" />
        </F>
      </div>
    </Section>
  );
};

export const PdfSection = ({ form, set }) => (
  <Section title="PDF buste paga" testId="settings-pdf-section" hint="La password con cui ricevi ogni mese il PDF protetto.">
    <F id="pdfpw" label="Password del PDF">
      <Input id="pdfpw" type="password" value={form.pdf_password} onChange={(e) => set("pdf_password", e.target.value)} autoComplete="new-password"
        placeholder={form.has_pdf_password ? "•••••••• (salvata, lascia vuoto per non cambiarla)" : "Inserisci la password"} data-testid="settings-pdf-password-input" />
    </F>
    <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-4 py-3">
      <div>
        <p className="text-sm font-medium">Proteggi le buste paga inviate</p>
        <p className="text-xs text-slate-500">Ogni PDF spedito resta protetto con la stessa password.</p>
      </div>
      <Switch checked={form.protect_output} onCheckedChange={(v) => set("protect_output", v)} data-testid="protect-output-switch" />
    </div>
  </Section>
);

export const AutoSendSection = ({ form, set }) => (
  <Section title="Invio automatico" testId="settings-autosend-section"
    hint="Se attivo, le buste paga partono da sole subito dopo il caricamento, ma solo quando tutte sono abbinate a un dipendente attivo con email. In caso contrario resta il controllo manuale.">
    <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-4 py-3">
      <div>
        <p className="text-sm font-medium">Invia automaticamente dopo il caricamento</p>
        <p className="text-xs text-slate-500">Richiede il server SMTP configurato e il 100% delle buste abbinate.</p>
      </div>
      <Switch checked={form.auto_send} onCheckedChange={(v) => set("auto_send", v)} data-testid="auto-send-switch" />
    </div>
  </Section>
);

export const ImapSection = ({ form, set }) => (
  <Section title="Server di lettura (IMAP) e cosa controllare" testId="settings-imap-section"
    hint="Server compilato automaticamente. Qui scegli quali mittenti controllare, in quali cartelle e quanto indietro andare nei controlli successivi.">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <F id="imap_host" label="Server IMAP"><Input id="imap_host" value={form.imap_host} onChange={(e) => set("imap_host", e.target.value)} placeholder="imap.gmail.com" data-testid="imap-host-input" /></F>
      <F id="imap_user" label="Utente (vuoto = utente SMTP)"><Input id="imap_user" value={form.imap_user} onChange={(e) => set("imap_user", e.target.value)} autoComplete="off" data-testid="imap-user-input" /></F>
      <F id="imap_pw" label={form.has_imap_password ? "Password (salvata, lascia vuoto per non cambiarla)" : "Password (vuota = password SMTP)"}>
        <Input id="imap_pw" type="password" value={form.imap_password} onChange={(e) => set("imap_password", e.target.value)} autoComplete="new-password" data-testid="imap-password-input" />
      </F>
      <F id="sender" label="Mittenti da controllare (separati da virgola, vuoto = tutti)" className="sm:col-span-2">
        <Textarea id="sender" rows={2} value={form.contracts_sender} onChange={(e) => set("contracts_sender", e.target.value)}
          placeholder="studioarenella@studioarenella.it, umbertoauletta@hotmail.it" data-testid="contracts-sender-input" />
      </F>
      <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-4 py-3 sm:col-span-2">
        <div><p className="text-sm font-medium">Includi anche la Posta inviata</p><p className="text-xs text-slate-500">Cerca gli allegati anche nelle email che hai inviato tu a questi indirizzi.</p></div>
        <Switch checked={form.imap_include_sent} onCheckedChange={(v) => set("imap_include_sent", v)} data-testid="imap-include-sent-switch" />
      </div>
      <F id="months" label="Mesi indietro per i controlli successivi (il primo legge tutto)"><Input id="months" type="number" min={1} max={120} value={form.contracts_months} onChange={(e) => set("contracts_months", e.target.value)} data-testid="contracts-months-input" /></F>
      <F id="folder" label="Dove cercare">
        <Select value={form.imap_folder || "inbox"} onValueChange={(v) => set("imap_folder", v)}>
          <SelectTrigger id="folder" data-testid="imap-folder-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="inbox">Solo Posta in arrivo</SelectItem>
            <SelectItem value="all">Tutta la casella (anche archiviate)</SelectItem>
          </SelectContent>
        </Select>
      </F>
    </div>
  </Section>
);

export const AutomationSection = ({ form, set, isAdmin = true }) => (
  <Section title="Importazione automatica e avvisi" testId="settings-automation-section"
    hint="Ogni giorno alle 17:00 (ora italiana) la casella viene controllata e vengono inviati gli avvisi di scadenza. Il primo controllo legge tutta la cronologia dei mittenti indicati.">
    <div className="space-y-3">
      <Toggle label="Abilita importazione dalle email" hint="Controllo quotidiano alle 17:00: importa contratti e UNILAV nuovi, il resto finisce in Da verificare." checked={form.auto_sync} onChange={(v) => set("auto_sync", v)} testId="auto-sync-switch" />
      {isAdmin && <Toggle label="Cessazione automatica" hint="Quando arriva un UNILAV di cessazione, il dipendente viene segnato come cessato." checked={form.auto_cessazione} onChange={(v) => set("auto_cessazione", v)} testId="auto-cessazione-switch" />}
      <Toggle label="Avviso contratti in scadenza" hint="Email giornaliera, solo se c'è almeno un contratto in scadenza entro l'anticipo indicato." checked={form.alerts_enabled} onChange={(v) => set("alerts_enabled", v)} testId="alerts-enabled-switch" />
      <Toggle label="Riepilogo dopo ogni invio cedolini" hint="Email con inviate, fallite e motivo degli errori dopo ogni invio." checked={form.send_summary} onChange={(v) => set("send_summary", v)} testId="send-summary-switch" />
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <F id="alert_email" label="Email per avvisi e riepiloghi (vuota = email mittente)">
        <Input id="alert_email" type="email" value={form.alert_email} onChange={(e) => set("alert_email", e.target.value)} data-testid="alert-email-input" />
      </F>
      <F id="alert_days" label="Anticipo avvisi scadenze (giorni)">
        <Input id="alert_days" type="number" min={1} max={365} value={form.alert_days ?? 30} onChange={(e) => set("alert_days", e.target.value)} data-testid="alert-days-input" />
      </F>
      {!isAdmin && <F id="sender_op" label="Mittenti da controllare (separati da virgola)" className="sm:col-span-2">
        <Textarea id="sender_op" rows={2} value={form.contracts_sender} onChange={(e) => set("contracts_sender", e.target.value)} data-testid="contracts-sender-input" />
      </F>}
    </div>
  </Section>
);

const Toggle = ({ label, hint, checked, onChange, testId }) => (
  <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-4 py-3">
    <div><p className="text-sm font-medium">{label}</p><p className="text-xs text-slate-500">{hint}</p></div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export const TemplateSection = ({ form, set }) => (
  <Section title="Messaggio email" testId="settings-template-section"
    hint="Variabili disponibili: {NOME}, {COGNOME}, {MESE}, {AZIENDA}.">
    <F id="subject" label="Oggetto">
      <Input id="subject" value={form.email_subject} onChange={(e) => set("email_subject", e.target.value)} data-testid="email-subject-input" />
    </F>
    <F id="body" label="Testo">
      <Textarea id="body" rows={8} value={form.email_body} onChange={(e) => set("email_body", e.target.value)} data-testid="email-body-input" />
    </F>
  </Section>
);
